import React, { useState, useEffect } from 'react';
import {
	Button,
	Menu,
	Avatar,
	MenuItem,
	Slide,
	Badge,
	ListItem,
	List,
	ListItemText,
	ListItemIcon,
	Divider
} from '@material-ui/core';
import {
	AccountCircleOutlined,
	ShoppingCartOutlined,
	RateReviewOutlined,
	ExitToAppOutlined,
	MailOutline,
	LockOutlined,
	Launch
} from '@material-ui/icons';

import { useTheme } from '@material-ui/core/styles';

import PackageStore from '../../../common/util/PackageStore';
import UIHelper from '../../../common/UIHelper';
import Constants from '../../../common/Constants';
import TranslationHelper from '../../../common/TranslationHelper';
import SessionHelper from '../../../common/SessionHelper';
import AjaxHelper from '../../../common/AjaxHelper';
import SocketIoHelper from '../../../common/SocketIoHelper';

let socket = null;
let showNotification = true;

export default function ProfileMenu(props) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [badgeNumber, setBadgeNumber] = useState(null);
	const theme = useTheme();
	const [loggedInUser, setLoggedInUser] = useState(null);
	const loggedIn = SessionHelper.getSessionUser();
	const handleMenuClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	useEffect(() => {
		let isMounted = true;
		(async () => {
			// get logged in user
			if (loggedIn) {
				const profile = await AjaxHelper.invokeServerAPIPromise('/api/profile/get');
				if (isMounted) setLoggedInUser(profile);
				try {
					// get socket
					socket = SocketIoHelper.socket;

					// get all available conversations on connection
					socket.emit('getConversations', profile._id);

					// set total number of unread notifications
					socket.on('sendConversationsToPublic', (data) => {
						let notificationsTotal = null;
						data.forEach((el) => {
							notificationsTotal += el.notifications && el.notifications.length
								? el.notifications.length
								: 0;
						});
						if (isMounted) setBadgeNumber(notificationsTotal);
					});

					// update total number of unread notifications when user read them
					socket.on('updateNotificationCount', (count) => {
						if (isMounted) setBadgeNumber((badgeNumber) => badgeNumber - count);
					});

					// display popup and update badge when new message arrives
					socket.on('publicAppNotification', () => {
						if (showNotification) {
							UIHelper.showInfoMessage('You have a new message!');
						}
						if (isMounted) setBadgeNumber((badgeNumber) => badgeNumber + 1);

						showNotification = false;
						setTimeout(() => {
							showNotification = true;
						}, 5000);
					});
				} catch (error) {
					console.log('Error retrieving messageServer', error);
				}
			}
		})();
		return () => { isMounted = false; };
	}, []);

	const mobileMenu = (
		props.loggedIn
			? (
				<>
					<Avatar
						alt="Profile Picture"
						src={loggedInUser && loggedInUser.avatar}
						onClick={handleMenuClick}
						style={{
							cursor: 'pointer', width: '60px', height: '60px', margin: 'auto'
						}}
					/>
					<List>
						{PackageStore.isPackageSubscribed(Constants.PACKAGE_NAMESPACE_MESSAGING)
					&& (
						<ListItem button onClick={() => { window.location = '/messages'; }}>
							<ListItemIcon>
								<Badge badgeContent={badgeNumber} color="error" style={{ marginRight: '10px' }}>
									<MailOutline style={{ fill: theme.palette.primary.main, fontSize: '25px' }} />
								</Badge>
							</ListItemIcon>
							<ListItemText primary="Messages" />
						</ListItem>
					)}
						<ListItem button onClick={() => { window.location = '/orders'; }} data-cy="orders">
							<ListItemIcon>
								<ShoppingCartOutlined style={{ fill: theme.palette.primary.main, fontSize: '25px' }} />
							</ListItemIcon>
							<ListItemText primary="Orders" />
						</ListItem>
						<ListItem button onClick={() => { window.location = '/reviews'; }} data-cy="reviews">
							<ListItemIcon>
								<RateReviewOutlined style={{ fill: theme.palette.primary.main, fontSize: '25px' }} />
							</ListItemIcon>
							<ListItemText primary="Reviews" />
						</ListItem>
						<ListItem
							button
							data-cy="profile"
							onClick={() => { window.location = '/profile/view'; }}
						>
							<ListItemIcon>
								<AccountCircleOutlined style={{ fill: theme.palette.primary.main, fontSize: '25px' }} />
							</ListItemIcon>
							<ListItemText primary="Profile" />
						</ListItem>
						<ListItem button onClick={() => props.handleSignOut()} data-cy="logout">
							<ListItemIcon>
								<ExitToAppOutlined style={{ fill: theme.palette.primary.main, fontSize: '25px' }} />
							</ListItemIcon>
							<ListItemText primary="Logout" />
						</ListItem>
						{props.adminAppURL && (
							<>
								<Divider style={{ margin: '4px 0px' }} />
								<ListItem
									button
									onClick={() => { window.location = props.adminAppURL; }}
								>
									<ListItemIcon>
										<Launch style={{ fill: theme.palette.success.main, fontSize: '25px' }} />
									</ListItemIcon>
									<ListItemText primary="Admin App" />
								</ListItem>
							</>
						)}
					</List>
				</>
			)
			: (
				<List>
					<ListItem
						button
						onClick={!props.loginLayout || props.loginLayout === 'default'
							? props.handleOpenSignIn
							: (() => { window.location.href = '/login'; })}
						data-cy="signInButton"
					>
						<ListItemIcon>
							<LockOutlined style={{ fill: theme.palette.primary.main, fontSize: '25px' }} />
						</ListItemIcon>
						<ListItemText primary={TranslationHelper.getValue('customer_account.modal.navItem.signIn')} />
					</ListItem>
				</List>
			)
	);

	const desktopMenu = (
		props.loggedIn
			? (
				<>
					<Badge badgeContent={badgeNumber} color="error" overlap="circular">
						<Avatar
							alt="Profile Picture"
							src={loggedInUser && loggedInUser.avatar}
							onClick={handleMenuClick}
							style={{
								cursor: 'pointer', width: '50px', height: '50px', marginLeft: '10px'
							}}
							data-cy="profileMenu"
						/>
					</Badge>
					<Menu
						id="simple-menu"
						anchorEl={anchorEl}
						keepMounted
						open={Boolean(anchorEl)}
						onClose={handleMenuClose}
						getContentAnchorEl={null}
						disableScrollLock
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						TransitionComponent={Slide}
					>
						{PackageStore.isPackageSubscribed(Constants.PACKAGE_NAMESPACE_MESSAGING)
					&& (
						<MenuItem
							onClick={() => { handleMenuClose(); window.location = '/messages'; }}
							style={{ padding: '10px 20px' }}
							data-cy="messages"
						>
							<Badge badgeContent={badgeNumber} color="error" style={{ marginRight: '10px' }}>
								<MailOutline style={{ fill: theme.palette.primary.main, fontSize: '25px' }} />
							</Badge>
							Messages
						</MenuItem>
					)}
						<MenuItem
							onClick={() => { handleMenuClose(); window.location = '/orders'; }}
							style={{ padding: '10px 20px' }}
							data-cy="orders"
						>
							<ShoppingCartOutlined
								style={{ marginRight: '10px', fill: theme.palette.primary.main, fontSize: '25px' }}
							/>
							Orders
						</MenuItem>
						<MenuItem
							onClick={() => { handleMenuClose(); window.location = '/reviews'; }}
							style={{ padding: '10px 20px' }}
							data-cy="reviews"
						>
							<RateReviewOutlined
								style={{ marginRight: '10px', fill: theme.palette.primary.main, fontSize: '25px' }}
							/>
							Reviews
						</MenuItem>
						<MenuItem
							onClick={() => {
								handleMenuClose();
								window.location = '/profile/view';
							}}
							style={{ padding: '10px 20px' }}
							data-cy="profile"
						>
							<AccountCircleOutlined
								style={{ marginRight: '10px', fill: theme.palette.primary.main, fontSize: '25px' }}
							/>
							Profile
						</MenuItem>
						<MenuItem
							onClick={() => { handleMenuClose(); props.handleSignOut(); }}
							style={{ padding: '10px 20px' }}
							data-cy="logout"
						>
							<ExitToAppOutlined
								style={{ marginRight: '10px', fill: theme.palette.primary.main, fontSize: '25px' }}
							/>
							Logout
						</MenuItem>
						{props.adminAppURL && (
							<>
								<Divider style={{ margin: '4px 0px' }} />
								<MenuItem
									onClick={() => {
										handleMenuClose();
										window.location = props.adminAppURL;
									}}
									style={{ padding: '10px 20px' }}
								>
									<Launch
										style={{
											marginRight: '10px',
											fill: theme.palette.success.main,
											fontSize: '25px'
										}}
									/>
									Admin App
								</MenuItem>
							</>
						)}
					</Menu>
				</>
			)
			: (
				<div style={{ marginLeft: '10px' }}>
					<Button
						variant="outlined"
						color="primary"
						data-cy="signInButton"
						style={{ outline: 0 }}
						onClick={!props.loginLayout || props.loginLayout === 'default'
							? props.handleOpenSignIn
							: ((e) => { e.preventDefault(); window.location.href = '/login'; })}
					>
						{TranslationHelper.getValue('customer_account.modal.navItem.signIn')}
					</Button>
				</div>
			)
	);

	return (
		props.mobileView ? mobileMenu : desktopMenu
	);
}
